// src/frontend_react/src/utils/cryptoHelper.js

/**
 * Generates an HMAC-SHA256 hash.
 *
 * @param {string} message The message string to hash.
 * @param {string} secretKeyString The secret key string. If it's a hex string (e.g., from backend's hexdigest),
 *                                 it will be converted to raw bytes. Otherwise, it's UTF-8 encoded.
 * @returns {Promise<string>} The hex-encoded HMAC hash.
 * @throws {Error} If message or secretKeyString is missing, empty, or hashing fails.
 */
export async function hmacSha256(message, secretKeyString) {
  if (message === undefined || message === null || secretKeyString === undefined || secretKeyString === null) {
    console.error('HMAC-SHA256: Message or secret key is missing or null/undefined.');
    throw new Error('Message and secret key are required and cannot be null/undefined for HMAC-SHA256.');
  }

  // Ensure inputs are strings and not empty after conversion
  const Smessage = String(message);
  const SsecretKeyString = String(secretKeyString);

  if (!Smessage) {
    console.error('HMAC-SHA256: Message is empty after string conversion.');
    throw new Error('Message must not be empty for HMAC-SHA256.');
  }
  if (!SsecretKeyString) {
    console.error('HMAC-SHA256: Secret key is empty after string conversion.');
    throw new Error('Secret key must not be empty for HMAC-SHA256.');
  }

  const encoder = new TextEncoder();
  let keyData;

  // IMPORTANT: This is the correct logic for ephemeralHashingKey which is a hex string.
  // It checks if the string appears to be a hex string and converts it to bytes.
  // Otherwise, it assumes it's a regular string to be UTF-8 encoded.
  if (/^[0-9a-fA-F]+$/.test(SsecretKeyString) && SsecretKeyString.length % 2 === 0) {
    // It's a hex string, convert to Uint8Array
    const bytes = new Uint8Array(SsecretKeyString.length / 2);
    for (let i = 0; i < SsecretKeyString.length; i += 2) {
      bytes[i / 2] = parseInt(SsecretKeyString.substring(i, i + 2), 16);
    }
    keyData = bytes.buffer; // Use ArrayBuffer for importKey
  } else {
    // Assume it's a passphrase-like string to be encoded
    keyData = encoder.encode(SsecretKeyString);
  }

  const messageData = encoder.encode(Smessage);

  try {
    const cryptoKey = await window.crypto.subtle.importKey(
      'raw', // format
      keyData, // keyData (ArrayBuffer or TypedArray)
      { name: 'HMAC', hash: 'SHA-256' }, // algorithm
      false, // extractable
      ['sign'] // usages
    );

    const signatureBuffer = await window.crypto.subtle.sign(
      'HMAC',
      cryptoKey,
      messageData
    );

    const hashArray = Array.from(new Uint8Array(signatureBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Error in hmacSha256:', error);
    // Provide more context for common SubtleCrypto errors
    if (error.name === 'OperationError' && SsecretKeyString.length === 0) {
        throw new Error('HMAC key cannot be empty or invalid.');
    }
    throw error; // Re-throw the original error after logging
  }
}

/**
 * Calculates a SHA-256 hash of a given string.
 *
 * @param {string} text The string to hash.
 * @returns {Promise<string>} The hex-encoded SHA-256 hash.
 * @throws {Error} If input is not a string or hashing fails.
 */
export const calculateStringHash = async (text) => {
  if (typeof text !== 'string') {
    throw new Error('Input must be a string for calculateStringHash');
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  try {
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  } catch (error) {
    console.error('Error in calculateStringHash:', error);
    throw error;
  }
};

/**
 * Calculates a SHA-256 hash of a given File object.
 *
 * @param {File} file The File object to hash.
 * @returns {Promise<string>} The hex-encoded SHA-256 hash.
 * @throws {Error} If no file is provided or file reading/hashing fails.
 */
export const calculateFileHash = async (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file provided for hashing."));
      return;
    }
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const buffer = event.target.result;
        if (!buffer) {
          reject(new Error("File content is empty or could not be read into buffer for hashing."));
          return;
        }
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        resolve(hashHex);
      } catch (error) {
        console.error("Error during file hash calculation (onload):", error);
        reject(error);
      }
    };
    reader.onerror = (error) => {
        console.error("Error reading file for hashing (onerror):", error);
        reject(error);
    };
    reader.readAsArrayBuffer(file);
  });
};


// --- AES Encryption/Decryption for Files (Client-Side) ---
// Based on typical AES-256-CBC pattern: Key derivation from password/secret string, IV, PBKDF2.

const AES_KEY_SIZE_BYTES = 32; // 256 bits
const AES_IV_LENGTH_BYTES = 16; // 128 bits
const PBKDF2_SALT_LENGTH_BYTES = 16;
const PBKDF2_ITERATIONS = 100000; // Match backend's crypto_utils.py

/**
 * Derives a cryptographic key from a secret string using PBKDF2.
 * @param {string} secretString The secret string (e.g., OUTPUT_ENCRYPTION_KEY).
 * @param {Uint8Array} salt A unique salt for PBKDF2.
 * @param {number} keyLengthBytes Desired key length in bytes.
 * @returns {Promise<CryptoKey>} Derived key as a Web Crypto API CryptoKey.
 */
async function deriveKeyFromSecret(secretString, salt, keyLengthBytes) {
  const encoder = new TextEncoder();
  const baseKey = await window.crypto.subtle.importKey(
    'raw',
    encoder.encode(secretString),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );

  return window.crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-CBC', length: keyLengthBytes * 8 }, // Key length in bits
    true, // Extractable
    ['encrypt', 'decrypt'],
  );
}

/**
 * Encrypts data using AES-256-CBC.
 * Prepends PBKDF2 salt and IV to the ciphertext.
 * @param {ArrayBuffer} data The data to encrypt (e.g., from a CSV string converted to ArrayBuffer).
 * @param {string} secretString The secret key string (e.g., OUTPUT_ENCRYPTION_KEY).
 * @returns {Promise<ArrayBuffer>} Encrypted data as ArrayBuffer (includes salt + IV + ciphertext).
 */
export async function aesEncryptData(data, secretString) {
  if (!data || !secretString) {
    throw new Error('Data and secret string are required for AES encryption.');
  }

  const pbkdf2Salt = window.crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_LENGTH_BYTES));
  const iv = window.crypto.getRandomValues(new Uint8Array(AES_IV_LENGTH_BYTES)); // 16 bytes for AES-CBC

  const aesKey = await deriveKeyFromSecret(secretString, pbkdf2Salt, AES_KEY_SIZE_BYTES);

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv: iv,
    },
    aesKey,
    data,
  );

  // Combine PBKDF2 salt, IV, and encrypted data into a single ArrayBuffer
  const combined = new Uint8Array(pbkdf2Salt.byteLength + iv.byteLength + encryptedBuffer.byteLength);
  combined.set(pbkdf2Salt, 0);
  combined.set(iv, pbkdf2Salt.byteLength);
  combined.set(new Uint8Array(encryptedBuffer), pbkdf2Salt.byteLength + iv.byteLength);

  return combined.buffer;
}

/**
 * Decrypts data using AES-256-CBC.
 * Expects data to be prefixed with PBKDF2 salt and IV.
 * @param {ArrayBuffer} encryptedData The encrypted data (salt + IV + ciphertext).
 * @param {string} secretString The secret key string (e.g., OUTPUT_ENCRYPTION_KEY).
 * @returns {Promise<ArrayBuffer>} Decrypted data as ArrayBuffer.
 */
export async function aesDecryptData(encryptedData, secretString) {
  if (!encryptedData || !secretString) {
    throw new Error('Encrypted data and secret string are required for AES decryption.');
  }
  if (encryptedData.byteLength < PBKDF2_SALT_LENGTH_BYTES + AES_IV_LENGTH_BYTES) {
    throw new Error('Encrypted data is too short; missing salt or IV.');
  }

  const combined = new Uint8Array(encryptedData);
  const pbkdf2Salt = combined.slice(0, PBKDF2_SALT_LENGTH_BYTES);
  const iv = combined.slice(PBKDF2_SALT_LENGTH_BYTES, PBKDF2_SALT_LENGTH_BYTES + AES_IV_LENGTH_BYTES);
  const ciphertext = combined.slice(PBKDF2_SALT_LENGTH_BYTES + AES_IV_LENGTH_BYTES);

  const aesKey = await deriveKeyFromSecret(secretString, pbkdf2Salt, AES_KEY_SIZE_BYTES);

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    {
      name: 'AES-CBC',
      iv: iv,
    },
    aesKey,
    ciphertext,
  );

  return decryptedBuffer;
}